import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { from, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { ThemeService } from 'src/app/services/theme.service';
import { StacSessionService } from 'src/app/services/stac-session.service';
import { environment } from 'src/environments/environment';

/**
 * Embeds the STAC data browser (plans/stac-frontend.md phase F3).
 *
 * The browser is a separate Vue application served same-origin under `/stac/` — by the
 * ng serve proxy in dev, by the nginx `location /stac/` rule in prod. It is reached
 * through an iframe rather than mounted directly (decision D3), so its router and its
 * Bootstrap build cannot collide with ours.
 *
 * StacSessionService establishes and renews the HttpOnly read cookie. This component
 * mounts a token-free iframe and ends it when the platform session expires.
 */
@Component({
  selector: 'app-stac-browser',
  templateUrl: './stac-browser.component.html',
  styleUrls: ['./stac-browser.component.scss'],
})
export class StacBrowserComponent implements OnInit, OnDestroy {
  /** Floor for the measured height, so an overflowing shell cannot collapse the frame. */
  private static readonly MIN_FRAME_HEIGHT = 360;

  loading = true;
  hasError = false;
  errorMessage = 'Unable to open the data browser right now.';

  /**
   * Written exactly once, after the token resolves, and never reassigned while the frame
   * is mounted. The template guards on it with *ngIf so the `src` attribute is set a
   * single time: re-pointing a live iframe reloads the app, and a reload fires the
   * browser's `resetPage`, which discards the user's active search (decision D4).
   */
  stacUrl: string | null = null;

  /** True once the frame is confirmed to be showing the platform's theme. */
  themed = false;

  @ViewChild('stacFrame') private frame: ElementRef<HTMLIFrameElement>;
  @ViewChild('pageWrap') private pageWrap: ElementRef<HTMLElement>;

  private readonly destroy$ = new Subject<void>();
  private readonly jwtHelper = new JwtHelperService();
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  /** The platform's theme, for the waiting state's logo and for the frame (applyTheme). */
  darkMode = false;
  private themeObserver: MutationObserver | null = null;

  constructor(
    private session: StacSessionService,
    private authService: AuthService,
    private themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    this.session.error$.pipe(takeUntil(this.destroy$)).subscribe(message => {
      this.hasError = !!message;
      if (message) this.errorMessage = message;
    });
    this.session.accountChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.stacUrl = null;
    });
    this.session.home$.pipe(takeUntil(this.destroy$)).subscribe(() => this.goHome());
    this.openBrowser();

    // The frame is a separate document and cannot see a class appearing on our <body>,
    // so the platform has to tell it. ThemeService replays the current value, which
    // matters because this component mounts long after the theme was chosen at login.
    this.themeService.darkMode$.pipe(takeUntil(this.destroy$)).subscribe((dark) => {
      this.darkMode = dark;
      this.applyTheme();
    });
  }

  /**
   * Fires when the frame's document exists — but NOT when the embedded app is ready.
   * Measured: at this point `data-bs-theme` is still absent, and the app mounts later
   * and stamps its own default over anything written here. So this starts a watcher
   * rather than doing a one-shot write.
   */
  onFrameLoad(): void {
    this.watchFrameTheme();
    this.applyTheme();
    this.sizeFrame();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.sizeFrame();
  }

  /**
   * Size the frame to exactly the space the viewport has left, so the platform page
   * never gains a scrollbar of its own and the only one on screen belongs to the
   * frame's own content.
   *
   * Measured rather than calculated. The obvious `height: calc(100vh - 220px)` needs a
   * constant for the shell's chrome, and that constant is wrong the moment a banner
   * appears, the header wraps, or the zoom changes — the symptom being two stacked
   * scrollbars, which is what this replaces.
   *
   * Two passes, because a single measurement cannot work here. The shell pins
   * `.page-wrapper` to `height: 100vh`, so asking "how tall is everything else?" always
   * answers "a whole viewport" no matter what we do — collapsing ourselves to measure
   * the remainder yields zero and clamps the frame to its floor, which is worse than
   * the hardcoded calc it replaced.
   *
   * So: reach for the whole space below our top edge, then measure how much the page
   * actually overflows and give exactly that back. Whatever sits underneath us — a
   * footer, padding, a future banner — is accounted for by the overflow it causes,
   * without this needing to know it exists.
   */
  private sizeFrame(): void {
    const el = this.pageWrap?.nativeElement;
    if (!el) {
      return;
    }

    const floor = StacBrowserComponent.MIN_FRAME_HEIGHT;
    // `document.scrollingElement`, not `documentElement`: which element actually scrolls
    // depends on the rendering mode, and `documentElement.clientHeight` is NOT reliably
    // the viewport — in quirks mode it equals scrollHeight, so the overflow below reads
    // zero and pass 2 silently never runs. Measured, not assumed.
    const scroller = document.scrollingElement || document.documentElement;

    // Pass 1 — claim everything from our top edge to the bottom of the viewport.
    const claimed = Math.max(window.innerHeight - el.getBoundingClientRect().top, floor);
    el.style.height = `${claimed}px`;

    // Pass 2 — hand back whatever that made the page overflow by. Compared against
    // window.innerHeight, which is unambiguously the viewport.
    const overflow = scroller.scrollHeight - window.innerHeight;
    if (overflow > 0) {
      el.style.height = `${Math.max(claimed - overflow, floor)}px`;
    }
  }

  ngOnDestroy(): void {
    this.clearExpiryTimer();
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.openBrowser();
  }

  /**
   * The side menu's Data Catalog link, pressed while the catalog is already open. The router
   * ignores a link to the route it is on, so the frame is asked to go to its home page, and
   * eestacui (StacBrowser.vue) moves there with its own router. A message rather than
   * re-pointing `src`, which would reload the app (see stacUrl). Sent to our own origin only;
   * while the frame is still loading nobody hears it, and it is loading the home page anyway.
   */
  private goHome(): void {
    this.frame?.nativeElement?.contentWindow?.postMessage({ type: 'ee-stac:home' }, window.location.origin);
  }

  private openBrowser(): void {
    this.loading = true;
    this.hasError = false;
    this.clearExpiryTimer();

    from(this.session.ensure())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (!this.stacUrl) this.stacUrl = environment.stacUiUrl;
          this.loading = false;
          this.armExpiryTimer();
        },
        error: () => this.fail('Unable to open the data browser right now.'),
      });
  }

  private armExpiryTimer(): void {
    this.clearExpiryTimer();

    const expiresAt = this.sessionExpiry();
    if (!expiresAt) {
      return;
    }

    const remainingMs = expiresAt.getTime() - Date.now();
    if (remainingMs <= 0) {
      this.endSession();
      return;
    }
    this.expiryTimer = setTimeout(() => this.endSession(), remainingMs);
  }

  /**
   * Read from the platform JWT, NOT from the token endpoint's `expires_at`.
   *
   * The STAC token's TTL starts when this tab is opened; the platform session expires a
   * fixed period after LOGIN. Arming the timer from the former would fire it late by
   * however long the user had already been signed in — someone who logs in, works for
   * six hours and then opens this page would get a timer six hours after their session
   * was already dead.
   */
  private sessionExpiry(): Date | null {
    const raw = sessionStorage.getItem('jwttoken');
    if (!raw) {
      return null;
    }

    let token: string;
    try {
      token = JSON.parse(raw);
    } catch {
      token = raw;
    }

    // A failed login stores `{"Msg":"..."}` under the same key, so guard before decoding —
    // the same check the rest of the app makes.
    if (typeof token !== 'string' || token.indexOf('Msg') !== -1) {
      return null;
    }

    try {
      return this.jwtHelper.getTokenExpirationDate(token);
    } catch {
      return null;
    }
  }

  /**
   * AuthInterceptor cannot do this for us: it is an Angular HttpClient interceptor, and
   * the iframe issues its own requests straight to the STAC API. Without this timer a
   * user sitting on this page when their session expires sees a broken frame and no
   * redirect, until they touch something in the Angular shell.
   */
  private endSession(): void {
    this.clearExpiryTimer();
    this.stacUrl = null;
    // Match the shell's own timeout path, which broadcasts so other open tabs log out too.
    try {
      localStorage.setItem('logout-event', Date.now().toString());
    } catch {
      // Storage can be unavailable (private mode, quota); the logout below still runs.
    }
    this.authService.logoutAndReset();
  }

  /**
   * The two apps spell the same idea differently: the platform adds a `dark-theme`
   * class to its own body, the embedded browser reads `data-bs-theme` on its body
   * (bootstrap-vue-next's `useColorMode({ selector: 'body' })`). This is the translation.
   *
   * Setting the attribute rather than reaching into the embedded app's store is
   * deliberate: the attribute is a public rendering contract, whereas the store is an
   * internal that a future upstream release is free to reshape.
   *
   * Only possible because the frame is same-origin — the `/stac/` dev proxy locally,
   * the nginx `location /stac/` rule in production. If that ever changes this silently
   * becomes a no-op, hence the guard rather than an unhandled cross-origin throw.
   */
  private applyTheme(): void {
    const body = this.frame?.nativeElement?.contentDocument?.body;
    if (!body) {
      return;
    }
    const wanted = this.darkMode ? 'dark' : 'light';
    try {
      if (body.getAttribute('data-bs-theme') !== wanted) {
        body.setAttribute('data-bs-theme', wanted);
      }
      // Revealed only once the frame is showing the right theme, so a dark-mode user
      // never sees a flash of the embedded app's own default on the way through.
      this.themed = true;
    } catch {
      // Cross-origin, or the frame navigated away mid-write. Nothing to do.
    }
  }

  /**
   * The embedded app owns this attribute too — it writes its own default when it mounts
   * (after the load event) and rewrites it whenever its internal colour mode changes.
   * A single write from us is therefore not enough; measured, it gets overwritten
   * roughly a second later.
   *
   * So we watch and re-assert. `applyTheme` is a no-op when the value already matches,
   * which is what stops this from looping against our own writes.
   */
  private watchFrameTheme(): void {
    const body = this.frame?.nativeElement?.contentDocument?.body;
    if (!body || this.themeObserver) {
      return;
    }
    this.themeObserver = new MutationObserver(() => this.applyTheme());
    this.themeObserver.observe(body, { attributes: true, attributeFilter: ['data-bs-theme'] });
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private fail(message: string): void {
    this.errorMessage = message;
    this.hasError = true;
    this.loading = false;
  }
}
